/**
 * Textos da home. Altere aqui sem mexer nos templates.
 *
 * Regra de conteúdo: só descrever o que a plataforma faz hoje. Promessa nova (prazo, alerta,
 * integração, exportação) entra aqui depois de existir no produto.
 */

export interface NavLink {
  label: string;
  section: string;
}

export interface PainPoint {
  icon: string;
  title: string;
  description: string;
  solution: string;
  seal: string;
}

export interface ModuleFeature {
  eyebrow: string;
  title: string;
  description: string;
  items: string[];
}

export interface GalleryItem {
  label: string;
  mockup: 'dashboard' | 'documents' | 'education';
}

export interface Differentiator {
  icon: string;
  title: string;
  description: string;
}

export interface ExternalLink {
  label: string;
  href: string;
  icon: string;
}

export interface Resource {
  icon: string;
  title: string;
  description: string;
  href: string;
  /** Idioma do site de destino, quando não for português. */
  language?: string;
  links: ExternalLink[];
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FooterColumn {
  title: string;
  links: { label: string; section?: string; route?: string }[];
}

export const CONTACT_EMAIL = 'medviz@medviz.com.br';

/** Acreditações apoiadas, na ordem em que aparecem em todo o site. */
export const ACCREDITATIONS = ['JCI', 'ONA', 'Qmentum', 'ACSA'] as const;

export const NAV_LINKS: NavLink[] = [
  { label: 'Soluções', section: 'solucoes' },
  { label: 'Módulos', section: 'evidencias' },
  { label: 'Plataforma', section: 'modulos' },
  { label: 'Segurança', section: 'seguranca' },
  { label: 'Acreditações', section: 'materiais' }
];

export const HERO = {
  badges: [ACCREDITATIONS.join(' · '), 'Conformidade LGPD'],
  title: 'Evidências rastreáveis para auditorias clínicas.',
  description:
    'Centralize credenciamento, documentação, privilégios, avaliação de desempenho e educação continuada do corpo clínico, com trilha de auditoria em cada ação: evidências prontas para JCI, ONA, Qmentum e ACSA.',
  highlights: [
    'Documentos, cursos e vacinas com controle de vencimento',
    'Ciclos de avaliação de desempenho com contestação registrada',
    'Indicadores por hospital, equipe e especialidade'
  ],
  primaryCta: 'Agendar demonstração',
  secondaryCta: 'Conhecer os módulos'
};

export const PAIN_POINTS = {
  title: 'Prevenção de não conformidades na gestão do corpo clínico.',
  items: [
    {
      icon: 'assignment_late',
      title: 'Documentos vencidos',
      description:
        'Registros nos conselhos, títulos de especialista e comprovantes expiram sem que a gestão perceba. No Medviz, a documentação de cada profissional fica centralizada, validada e com vencimento controlado.',
      solution: 'Validação documental com pendências visíveis por profissional.',
      seal: 'Controle verificado'
    },
    {
      icon: 'history_edu',
      title: 'Falta de evidência',
      description:
        'Sem rastreabilidade, o trabalho da equipe fica invisível para o avaliador. O Medviz registra quem fez o quê, quando, em qual hospital e com qual evidência.',
      solution: 'Trilha de auditoria em cada ação relevante da plataforma.',
      seal: 'Pronto para auditoria'
    },
    {
      icon: 'verified',
      title: 'Avaliações irregulares',
      description:
        'Avaliações de desempenho fora do ciclo ou sem critério comprometem a governança clínica. Padronize fichas, critérios e prazos por especialidade.',
      solution: 'Ciclos com prazo, delegação de avaliadores e contestação registrada.',
      seal: 'Excelência clínica'
    }
  ] satisfies PainPoint[]
};

export const MODULES: ModuleFeature[] = [
  {
    eyebrow: 'Módulo 01',
    title: 'Credenciamento e dossiê digital do profissional',
    description:
      'Substitua pastas físicas por um cadastro único, em que cada profissional tem dados, conselho de classe, vínculos e documentos validados em um só lugar.',
    items: [
      'Envio de documentos pelo próprio profissional, com validação pela equipe do hospital.',
      'Cadastro provisório para plantonistas, com aprovação da Diretoria e prazo de validade.',
      'Termos e regimento assinados eletronicamente, com a versão registrada.'
    ]
  },
  {
    eyebrow: 'Módulo 02',
    title: 'Avaliação de desempenho baseada em evidências',
    description:
      'Transforme avaliações subjetivas em dados auditáveis, como pedem os programas de avaliação contínua de desempenho profissional (OPPE) das acreditações.',
    items: [
      'Fichas por especialidade, com critérios avaliados como conforme, parcialmente conforme ou não conforme.',
      'Ciclos de avaliação com prazos, delegação de avaliadores e contestação pelo profissional.',
      'Evidências ligadas ao critério: notificações, treinamentos, elogios e reclamações da ouvidoria.'
    ]
  },
  {
    eyebrow: 'Módulo 03',
    title: 'Escopo de atuação e privilégios clínicos',
    description:
      'Comprove que cada procedimento é realizado por um profissional formalmente autorizado para ele, naquele hospital.',
    items: [
      'Privilégios por especialidade, área de atuação e procedimento.',
      'Solicitação pelo profissional e concessão pela coordenação, com trilha registrada.',
      'Privilégios isolados por hospital: o que vale em uma unidade não se estende a outra.'
    ]
  },
  {
    eyebrow: 'Módulo 04',
    title: 'Educação continuada e saúde ocupacional',
    description:
      'Comprove os treinamentos obrigatórios e a situação vacinal do corpo clínico com evidência técnica, não com listas de presença em papel.',
    items: [
      'Cursos internos com pós-teste, link coletivo e identificação do participante pelo conselho de classe.',
      'Importação de listas de participação com conciliação automática dos profissionais.',
      'Controle de vacinas, doses e comprovantes, validados pela SCIH e pela Medicina do Trabalho.'
    ]
  }
];

export const GALLERY = {
  title: 'A plataforma por dentro.',
  description:
    'Painéis e fluxos pensados para a rotina da Diretoria Técnica, da Qualidade e do Relacionamento Médico.',
  cta: 'Ver módulos',
  items: [
    { label: 'Indicadores de conformidade', mockup: 'dashboard' },
    { label: 'Gestão documental', mockup: 'documents' },
    { label: 'Educação continuada', mockup: 'education' }
  ] satisfies GalleryItem[]
};

export const DIFFERENTIATORS = {
  title: 'Governança integrada do corpo clínico.',
  items: [
    { icon: 'smartphone', title: 'No celular', description: 'Documentos e cursos de qualquer lugar.' },
    { icon: 'account_tree', title: 'Trilha de auditoria', description: 'Quem fez o quê, quando e onde.' },
    { icon: 'domain', title: 'Multi-hospital', description: 'Dados e permissões isolados por unidade.' },
    { icon: 'draw', title: 'Assinatura', description: 'Termos assinados eletronicamente.' },
    { icon: 'gavel', title: 'LGPD', description: 'Dados sensíveis com acesso por perfil.' }
  ] satisfies Differentiator[]
};

export const RESOURCES = {
  title: 'Recursos para auditoria.',
  description:
    'Acesse as referências oficiais das acreditações que o Medviz apoia e prepare sua instituição com base nos requisitos de cada metodologia.',
  items: [
    {
      icon: 'public',
      title: 'Acreditação JCI',
      description:
        'Padrão internacional da Joint Commission International para qualidade e segurança do paciente: entenda o processo de acreditação.',
      href: 'https://www.jointcommission.org/en/accreditation',
      language: 'Conteúdo em inglês',
      links: [
        { label: 'Sobre a acreditação', href: 'https://www.jointcommission.org/en/accreditation', icon: 'info' },
        { label: 'Biblioteca', href: 'https://www.jointcommission.org/en/knowledge-library', icon: 'menu_book' }
      ]
    },
    {
      icon: 'verified',
      title: 'Acreditação ONA',
      description:
        'Metodologia brasileira da Organização Nacional de Acreditação: conheça a jornada, os níveis de acreditação e os requisitos para sua instituição.',
      href: 'https://ona.org.br/jornadas/acreditacao/sobre',
      links: [
        { label: 'Sobre a acreditação', href: 'https://ona.org.br/jornadas/acreditacao/sobre', icon: 'info' },
        { label: 'Biblioteca', href: 'https://ona.org.br/biblioteca', icon: 'menu_book' }
      ]
    },
    {
      icon: 'public',
      title: 'Acreditação Qmentum',
      description:
        'Programa internacional da Accreditation Canada, com padrões de governança, liderança e qualidade assistencial.',
      href: 'https://accreditation.ca/qmentum-global/international-accreditation/',
      language: 'Conteúdo em inglês',
      links: [
        {
          label: 'Sobre a acreditação',
          href: 'https://accreditation.ca/qmentum-global/international-accreditation/',
          icon: 'info'
        },
        { label: 'Programas Qmentum Global', href: 'https://accreditation.ca/qmentum-global/', icon: 'menu_book' }
      ]
    },
    {
      icon: 'verified',
      title: 'Acreditação ACSA',
      description:
        'Modelo da Agencia de Calidad Sanitaria de Andalucía, com padrões para centros hospitalares voltados à melhoria contínua.',
      href: 'https://www.sspa.juntadeandalucia.es/agenciadecalidadsanitaria/estandares/centros-hospitalarios/',
      language: 'Conteúdo em espanhol',
      links: [
        {
          label: 'Padrões para hospitais',
          href: 'https://www.sspa.juntadeandalucia.es/agenciadecalidadsanitaria/estandares/centros-hospitalarios/',
          icon: 'info'
        },
        {
          label: 'Certificação de qualidade',
          href: 'https://www.sspa.juntadeandalucia.es/agenciadecalidadsanitaria/certificacion-de-calidad/',
          icon: 'menu_book'
        }
      ]
    }
  ] satisfies Resource[]
};

export const FAQ = {
  title: 'Perguntas frequentes',
  items: [
    {
      question: 'Quais acreditações o Medviz apoia?',
      answer:
        'O Medviz organiza as evidências do corpo clínico pedidas por JCI, ONA, Qmentum e ACSA: credenciamento, privilégios, avaliação de desempenho, educação permanente e saúde ocupacional.'
    },
    {
      question: 'Quanto tempo leva a implantação?',
      answer:
        'Depende do número de profissionais e dos módulos contratados. A implantação inclui a configuração por hospital, a importação dos dados e o treinamento das equipes, com cronograma definido junto à instituição.'
    },
    {
      question: 'Os dados de um hospital ficam separados dos de outro?',
      answer:
        'Sim. Cada hospital é uma fronteira de segurança: o usuário só vê os dados das unidades a que tem acesso, e toda permissão é validada no servidor.'
    },
    {
      question: 'Atende outros profissionais além dos médicos?',
      answer:
        'Sim. A plataforma atende profissionais de diferentes conselhos de classe, como enfermagem, fisioterapia, nutrição e psicologia.'
    },
    {
      question: 'O que acontece com os dados ao fim do contrato?',
      answer:
        'A instituição recebe a exportação dos seus dados e documentos, em um processo estruturado e auditável.'
    }
  ] satisfies FaqItem[]
};

export const DEMO_REQUEST = {
  title: 'Excelência clínica com rastreabilidade total.',
  description:
    'Agende uma demonstração e veja como o Medviz organiza a governança e as evidências do seu corpo clínico.',
  sealTitle: 'Confidencialidade',
  sealText:
    '"Demonstrações feitas com perfis fictícios, para preservar o sigilo de cada instituição."',
  submit: 'Solicitar demonstração'
};

export const FOOTER = {
  tagline: 'Gestão do corpo clínico com evidências rastreáveis para acreditação hospitalar.',
  columns: [
    {
      title: 'Soluções',
      links: [
        { label: 'Credenciamento', section: 'evidencias' },
        { label: 'Privilégios', section: 'evidencias' },
        { label: 'Avaliação de desempenho', section: 'evidencias' },
        { label: 'Educação continuada', section: 'evidencias' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Política de privacidade', route: '/privacy-policy' },
        { label: 'Termos de uso', route: '/terms-use' },
        { label: 'Aviso legal', route: '/legal-notice' }
      ]
    },
    {
      title: 'Suporte',
      links: [
        { label: 'Perguntas frequentes', section: 'faq' },
        { label: 'Fale conosco', section: 'contato' }
      ]
    }
  ] as FooterColumn[],
  copyright: 'Medviz. Todos os direitos reservados.',
  signature: 'Governança clínica com evidência'
};
